import { isCurrentPlanOffer } from '../../../../commercial/presentation';
import { useFeatureCatalog } from '../../../../context/FeatureCatalogContext';
import { billingCycles, publishedBillingCycles } from '../../../../utils/billingCycles';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import BillingPanel from '../Subscription/BillingPanel';
import PackageStore from '../Subscription/PackageStore';
import CreditPackStore from '../Subscription/CreditPackStore';
import { Button, Badge, Alert } from 'react-bootstrap';
import SubscriptionPlanCard from '../../Common/SubscriptionPlanCard';
import {
  FileText, Building, Users, AlertTriangle, Clock,
  RefreshCw, Shield, HardDrive, Bot, History, BookOpen, Layers,
  Receipt
} from 'lucide-react';
import { format } from 'date-fns';
import { sv } from 'date-fns/locale';
import { creditDecimalToMicrocredits, creditPercentage as exactCreditPercentage, formatMicrocredits, hasMicrocredits } from '../../../../utils/creditUnits';

const getChangeTypeLabel = (changeType) => {
  const labels = {
    created: 'Skapad',
    plan_upgrade: 'Plan uppgraderad',
    plan_downgrade: 'Plan nedgraderad',
    cancelled: 'Avslutad',
    reactivated: 'Återaktiverad',
    trial_started: 'Provperiod startad',
    trial_ended: 'Provperiod avslutad',
    credits_purchased: 'Krediter köpta',
    credits_reset: 'Krediter återställda',
    addon_purchased: 'Tillägg köpt',
    addon_cancelled: 'Tillägg avslutat'
  };
  return labels[changeType] || 'Prenumerationen uppdaterades';
};

const formatSubscriptionDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'd MMMM yyyy', { locale: sv });
  } catch {
    return dateStr;
  }
};

// Subscription & features section — plan overview, resource usage, available
// add-ons and plans, and subscription history. Subscription data is lazy-loaded
// in the CompanySettings shell and passed in as props; modals (credit purchase,
// plan upgrade) live in the shell and are opened via the setShow* props.
const SubscriptionSection = ({
  entitlements,
  caps,
  stripeConfigured,
  cancelLoading,
  resourceUsage,
  plans,
  features,
  subscriptionHistory,
  handleCancelSubscription,
  refreshEntitlements,
  fetchSubscriptionData,
  onSelectPlan,
}) => {
  // Published prices come from the catalog for the selected billing period.
  const { companyId } = useParams();
  const { definitions, featureName } = useFeatureCatalog();
  const awaitingPlan = entitlements?.planSelectionRequired || entitlements?.billingAccess?.code === 'BILLING_PLAN_SELECTION_REQUIRED';
  const [selectedBillingPeriod, setBillingPeriod] = useState(entitlements?.billingCycle || 'monthly');
  useEffect(() => {
    setBillingPeriod(entitlements?.billingCycle || 'monthly');
  }, [companyId, entitlements?.billingCycle]);
  const availableCycles = publishedBillingCycles(plans);
  const billingPeriod = availableCycles.includes(selectedBillingPeriod) ? selectedBillingPeriod : availableCycles[0] || 'monthly';
  const balanceMicrocredits = entitlements?.aiCredits?.balanceMicrocredits || creditDecimalToMicrocredits(entitlements?.aiCredits?.balance || '0');
  const allocationMicrocredits = entitlements?.aiCredits?.periodAllocationMicrocredits
    || creditDecimalToMicrocredits(String(entitlements?.aiCredits?.periodAllocation || '0'));
  const creditPercentage = Math.round(exactCreditPercentage(balanceMicrocredits, allocationMicrocredits));

  return (
    <>
      <BillingPanel companyId={companyId} isOwner={caps.isOwner} entitlements={entitlements} configured={stripeConfigured}
        onRefresh={() => { refreshEntitlements(); fetchSubscriptionData(); }} />
      {/* Plan Overview */}
      <div className="modern-card" id="oversikt">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <Shield size={16} />
              {awaitingPlan ? 'Välj din prenumeration' : 'Prenumerationsöversikt'}
            </h2>
            <p className="section-subtitle">
              {awaitingPlan ? 'Ingen plan vald' : entitlements?.plan?.displayName || 'Ingen plan'}
              <Badge
                bg={entitlements?.status === 'active' ? 'success' : entitlements?.status === 'trial' ? 'info' : 'warning'}
                className="ms-2"
              >
                {awaitingPlan ? 'Väntar på planval' : entitlements?.status === 'active' ? 'Aktiv' :
                  entitlements?.status === 'trial' ? 'Provperiod' :
                    entitlements?.status === 'cancelled' ? 'Avslutad' :
                      ({ paused: 'Pausad', past_due: 'Betalning saknas', canceled: 'Avslutad' })[entitlements?.status] || entitlements?.status || 'Okänd'}
              </Badge>
              {!awaitingPlan && <span className="ms-3">
                {formatSubscriptionDate(entitlements?.periodStart)} – {formatSubscriptionDate(entitlements?.periodEnd)}
              </span>}
              {entitlements?.hasStripeSubscription && billingCycles[entitlements?.billingCycle] && (
                <span className="ms-3">Betalningsperiod: {billingCycles[entitlements.billingCycle].label}</span>
              )}
            </p>
          </div>
          <div className="action-buttons d-flex gap-2">
            {/* Owner-only per §2.4. caps.isOwner false hides the button
                entirely; backend still 403s the request via
                requireOwnerRole as the actual gate. */}
            {caps.isOwner && stripeConfigured && entitlements?.hasStripeSubscription && !entitlements?.cancelAtPeriodEnd && ['active','trial'].includes(entitlements?.status) && (
              <Button
                variant="outline-danger"
                size="sm"
                className="d-flex align-items-center gap-1"
                disabled={cancelLoading}
                onClick={handleCancelSubscription}
              >
                {cancelLoading ? 'Avslutar...' : 'Avsluta plan'}
              </Button>
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              className="d-flex align-items-center gap-1"
              onClick={() => { refreshEntitlements(); fetchSubscriptionData(); }}
            >
              <RefreshCw size={14} />
              Uppdatera
            </Button>
          </div>
        </div>
        <div className="modern-card-body">
          {awaitingPlan ? <p className="mb-0">Välj Bas eller Premium nedan för att komma igång. Funktioner och månadens AI-krediter blir tillgängliga när prenumerationen har bekräftats.</p> : (() => {
            const usage = resourceUsage?.usage;
            const limits = entitlements?.limits;
            const metrics = [
              { label: 'Användare', Icon: Users, current: usage?.usersCount ?? limits?.currentUsers ?? 0, limit: limits?.maxUsers },
              { label: 'Bankkonton', Icon: Building, current: usage?.bankAccounts ?? '–', limit: limits?.maxBankAccounts },
              { label: 'Anställda', Icon: Users, current: usage?.employees ?? '–', limit: limits?.maxEmployees },
              { label: 'Lagring', Icon: HardDrive, current: usage?.storageBytes ? (usage.storageBytes / (1024 * 1024 * 1024)).toFixed(2) : '0', limit: limits?.maxStorageGb, unit: 'GB' },
            ];

            const aiCredits = entitlements?.aiCredits || null;

            return (
              <div className="feature-cards">
                {metrics.map((m, idx) => {
                  const hasLimit = m.limit !== null && m.limit !== undefined;
                  return (
                    <div className="feature-card" key={idx}>
                      <div className="feature-icon core">
                        <m.Icon size={18} />
                      </div>
                      <div className="feature-content">
                        <h4 className="feature-name">{m.label}</h4>
                        <div className="feature-benefits">
                          <span className="benefit-tag">
                            {m.unit
                              ? `${m.current} / ${hasLimit ? m.limit : m.limit === null ? '∞' : '–'} ${m.unit}`
                              : `${m.current} / ${hasLimit ? m.limit : m.limit === null ? '∞' : '–'}`}
                            {m.suffix ? ` ${m.suffix}` : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {aiCredits && (
                  <div className="feature-card">
                    <div className="feature-icon core">
                      <Bot size={18} />
                    </div>
                    <div className="feature-content">
                      <div className="feature-header">
                        <h4 className="feature-name">LedgerLoop-krediter</h4>
                      </div>
                      <p className="feature-description" style={{ marginBottom: '0.25rem' }}>
                        {hasMicrocredits(aiCredits.fromPlanMicrocredits) && <span>Från plan: {formatMicrocredits(aiCredits.fromPlanMicrocredits)}</span>}
                        {hasMicrocredits(aiCredits.fromPlanMicrocredits) && hasMicrocredits(aiCredits.purchasedMicrocredits) && <span> · </span>}
                        {hasMicrocredits(aiCredits.purchasedMicrocredits) && <span>Köpta: {formatMicrocredits(aiCredits.purchasedMicrocredits)}</span>}
                        {hasMicrocredits(aiCredits.reservedMicrocredits) && <span> · Reserverade: {formatMicrocredits(aiCredits.reservedMicrocredits)}</span>}
                      </p>
                      {aiCredits.overrunPolicy === 'carry-forward-v1' && (
                        <p className="feature-description">
                          Om en påbörjad AI-uppgift använder fler krediter än du har kvar dras
                          överskjutande krediter från kommande månaders krediter eller ett kreditköp.
                        </p>
                      )}
                      {hasMicrocredits(aiCredits.deferredMicrocredits) && (
                        <p className="feature-description" role="status">
                          Överförbrukning: {formatMicrocredits(aiCredits.deferredMicrocredits)} krediter.
                          Beloppet dras från nästa månads krediter eller ett kreditköp.
                          Nya AI-uppgifter pausas tills det finns krediter tillgängliga.
                        </p>
                      )}
                      <div className="feature-benefits">
                        <span className="benefit-tag">
                          {formatMicrocredits(balanceMicrocredits)} / {formatMicrocredits(allocationMicrocredits)} kvar
                        </span>
                        {creditPercentage <= 25 && (
                          <span className="benefit-tag" style={{
                            background: creditPercentage <= 10 ? '#fef2f2' : '#fffbeb',
                            color: creditPercentage <= 10 ? '#991b1b' : '#92400e',
                            borderColor: creditPercentage <= 10 ? '#fca5a5' : '#fbbf24'
                          }}>
                            <AlertTriangle size={10} className="me-1" />
                            {creditPercentage <= 10 ? 'Nästan slut' : 'Lågt saldo'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <PackageStore companyId={companyId} isOwner={caps.isOwner} entitlements={entitlements}
        onRefresh={() => { refreshEntitlements(); fetchSubscriptionData(); }} />

      <CreditPackStore companyId={companyId} isOwner={caps.isOwner} entitlements={entitlements} configured={stripeConfigured} />

      {/* Available Plans */}
      <div className="modern-card" id="planer">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <Layers size={16} />
              Tillgängliga planer
            </h2>
            <p className="section-subtitle">
              Jämför planer och välj betalningsperiod.
            </p>
          </div>
        </div>
        <div className="modern-card-body subscription-plan-comparison">
          {stripeConfigured && entitlements?.status === 'past_due' && (
            <Alert variant="warning" className="mb-4">
              <AlertTriangle size={16} className="me-2" />
              <strong>Kontrollera din betalning.</strong> Se obetalda fakturor och betalsätt högst upp på sidan.
            </Alert>
          )}

          {availableCycles.length > 0 && <div className="subscription-plan-cycles" role="group" aria-label="Betalningsperiod">
            {availableCycles.map(cycle => <button type="button" key={cycle} aria-pressed={billingPeriod === cycle} onClick={() => setBillingPeriod(cycle)}>{billingCycles[cycle].label}</button>)}
          </div>}
          <p className="subscription-plan-note">Samtliga priser är exklusive moms. Du får granska ändringen innan du bekräftar.</p>

          <div className="subscription-plan-grid">
            {plans.filter(plan => plan.plan_name !== 'beta_free').map(plan => {
              const isCurrentOffer = isCurrentPlanOffer(plan, entitlements, billingPeriod);
              const currentContract = isCurrentOffer && entitlements?.hasStripeSubscription;
              const selectedPrice = plan.billing_prices?.find(price=>price.billingCycle===billingPeriod);
              return <SubscriptionPlanCard key={plan.plan_id} plan={plan} billingCycle={billingPeriod}
                trialEndsAt={entitlements?.trialEndsAt} headingAs="h3" isCurrent={isCurrentOffer}
                badge={plan.plan_name === 'professional' ? 'Populärast' : undefined}
                actionLabel={currentContract ? 'Nuvarande plan' : stripeConfigured ? 'Välj plan' : 'Kommer snart'}
                disabled={currentContract || !selectedPrice || !stripeConfigured || !caps.isOwner}
                onSelect={() => onSelectPlan(plan, billingPeriod)} />;
            })}
          </div>
        </div>
      </div>

      {/* Subscription History */}
      <div className="modern-card" id="historik">
        <div className="modern-card-header">
          <div>
            <h2 className="section-title">
              <History size={16} />
              Historik
            </h2>
            <p className="section-subtitle">
              Ändringar i din prenumeration
            </p>
          </div>
        </div>
        <div className="modern-card-body">
          {subscriptionHistory.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">
                <History size={32} />
              </div>
              <h4>Ingen prenumerationshistorik ännu</h4>
              <p>Ändringar i din prenumeration kommer att visas här</p>
            </div>
          ) : (
            <div>
              {subscriptionHistory.map((entry, index) => (
                <div key={index} className="d-flex mb-3 pb-3" style={{ borderBottom: index < subscriptionHistory.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                  <div className="me-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px', background: 'var(--rgba-primary-1)' }}>
                      <Clock size={16} className="text-primary" />
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <strong style={{ fontSize: '0.9rem' }}>{getChangeTypeLabel(entry.change_type)}</strong>
                        {entry.details && (
                          <p className="text-muted small mb-0 mt-1">
                            {typeof entry.details === 'string'
                              ? entry.details
                              : JSON.stringify(entry.details)}
                          </p>
                        )}
                      </div>
                      <small className="text-muted">
                        {formatSubscriptionDate(entry.changed_at)}
                      </small>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SubscriptionSection;
